import React, { createContext, useContext, useState, useEffect } from 'react';
import { AlertService } from '../services/alertService';
import { useAuth } from './AuthContext';
import { useNotification } from '../components/common/Notification';

const AlertContext = createContext();

export { AlertContext };

export const useAlerts = () => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error('useAlerts must be used within an AlertProvider');
  }
  return context;
};

export const AlertContextProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const { addNotification } = useNotification();
  
  // Initialiser AlertService avec le système de notification
  useEffect(() => {
    if (addNotification) {
      AlertService.setNotificationSystem(addNotification);
    }
  }, [addNotification]);

  const addAlert = (type, message, priority = 'medium', data = {}) => {
    if (!currentUser?.uid) return;
    
    // Convertir le type d'alerte en type de notification popup
    let notificationType = 'error'; // par défaut
    switch (type) {
      case 'error':
        notificationType = 'error';
        break;
      case 'warning':
        notificationType = 'warning';
        break;
      case 'security':
        notificationType = 'error';
        break;
      case 'system':
        notificationType = 'info';
        break;
      default:
        notificationType = 'info';
    }
    
    // Afficher la popup avec une durée plus longue pour les alertes critiques
    const duration = priority === 'critical' ? 10000 : priority === 'high' ? 7000 : 5000;
    
    addNotification(notificationType, message, {
      autoClose: true,
      duration: duration
    });
  };

  // Méthodes de convenance pour les types d'alertes courants
  const alertError = (title, message, data = {}) => {
    return addAlert(AlertService.ALERT_TYPES.ERROR, title, message, AlertService.PRIORITY_LEVELS.HIGH, data);
  };

  const alertWarning = (title, message, data = {}) => {
    return addAlert(AlertService.ALERT_TYPES.WARNING, title, message, AlertService.PRIORITY_LEVELS.MEDIUM, data);
  };

  const alertInfo = (title, message, data = {}) => {
    return addAlert(AlertService.ALERT_TYPES.INFO, title, message, AlertService.PRIORITY_LEVELS.LOW, data);
  };

  const alertSecurity = (title, message, data = {}) => {
    return addAlert(AlertService.ALERT_TYPES.SECURITY, title, message, AlertService.PRIORITY_LEVELS.CRITICAL, data);
  };

  const alertSystem = (title, message, data = {}) => {
    return addAlert(AlertService.ALERT_TYPES.SYSTEM, title, message, AlertService.PRIORITY_LEVELS.MEDIUM, data);
  };



  const value = {
    addAlert
  };

  return (
    <AlertContext.Provider value={value}>
      {children}
    </AlertContext.Provider>
  );
};

export default AlertContext;